// =============================================================================
// CAPABILITY DECISION SERVICE
// =============================================================================
//
// Implementeert de logica-regels voor de capability assessment wizard.
// Keuzedwingend, blokkerend bij incomplete beslissingen.
//
// =============================================================================

import {
  CapabilityDomain,
  CapabilityDecision,
  Responsibility,
  SkillLevel,
  ValidationResult,
} from './types';

// -----------------------------------------------------------------------------
// VALIDATION LOGIC
// -----------------------------------------------------------------------------

/**
 * Check if a domain decision is complete according to flow rules
 */
export function isDomainDecisionComplete(decision: CapabilityDecision): boolean {
  // Not required → always complete
  if (!decision.required) {
    return true;
  }

  // Required but no responsibility → incomplete
  if (!decision.responsibility) {
    return false;
  }

  // Responsibility = unknown → BLOCKS (must choose)
  if (decision.responsibility === 'unknown') {
    return false;
  }

  // Responsibility = other → complete (no further questions)
  if (decision.responsibility === 'other') {
    return true;
  }

  // Responsibility = self → need level
  if (decision.responsibility === 'self') {
    if (!decision.level) {
      return false;
    }

    // Level = advanced → complete
    if (decision.level === 'advanced') {
      return true;
    }

    // Level = basic | workable → need resolution
    if (decision.level === 'basic' || decision.level === 'workable') {
      return !!decision.resolution;
    }
  }

  return true;
}

/**
 * Check if a domain is blocking the flow (responsibility = unknown)
 */
export function isDomainBlocking(decision: CapabilityDecision): boolean {
  return decision.required && decision.responsibility === 'unknown';
}

/**
 * Validate all decisions - returns validation result
 */
export function validateAssessment(decisions: CapabilityDecision[]): ValidationResult {
  const missingDecisions: CapabilityDomain[] = [];
  const blockingDomains: CapabilityDomain[] = [];

  decisions.forEach(decision => {
    if (!isDomainDecisionComplete(decision)) {
      missingDecisions.push(decision.domain);
    }
    if (isDomainBlocking(decision)) {
      blockingDomains.push(decision.domain);
    }
  });

  return {
    isValid: missingDecisions.length === 0 && blockingDomains.length === 0,
    missingDecisions,
    blockingDomains,
  };
}

// -----------------------------------------------------------------------------
// DECISION FLOW HELPERS
// -----------------------------------------------------------------------------

/**
 * Check if we should ask for skill level (only if responsibility = self)
 */
export function shouldAskLevel(decision: CapabilityDecision): boolean {
  return decision.required && decision.responsibility === 'self';
}

/**
 * Check if we should ask for resolution (only if level = basic | workable)
 */
export function shouldAskResolution(decision: CapabilityDecision): boolean {
  if (!decision.required || decision.responsibility !== 'self') {
    return false;
  }
  return decision.level === 'basic' || decision.level === 'workable';
}

/**
 * Check if domain needs a milestone (based on decision)
 */
export function needsMilestone(decision: CapabilityDecision): boolean {
  if (!decision.required) {
    return false;
  }

  // Responsibility = other → needs delegation milestone
  if (decision.responsibility === 'other') {
    return true;
  }

  // Responsibility = self + resolution chosen → needs milestone
  if (decision.responsibility === 'self' && decision.resolution) {
    return true;
  }

  return false;
}

// -----------------------------------------------------------------------------
// PROGRESS TRACKING
// -----------------------------------------------------------------------------

/**
 * Calculate progress through wizard (0-100)
 */
export function calculateProgress(
  decisions: CapabilityDecision[],
  totalDomains: number
): number {
  const completedDomains = decisions.filter(isDomainDecisionComplete).length;
  return Math.round((completedDomains / totalDomains) * 100);
}

/**
 * Get next incomplete domain
 */
export function getNextIncompleteDomain(
  decisions: CapabilityDecision[]
): CapabilityDomain | null {
  const incomplete = decisions.find(d => !isDomainDecisionComplete(d));
  return incomplete ? incomplete.domain : null;
}
